import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';
import { AssistantAuditOperationApi } from '../Services/AgentApiProviderTypes';
import { TaskScheduleApiProvider, useTaskScheduleApi } from '../Services/TaskScheduleApiProvider';
import { formatDateUTC } from '../utils/formatDate';
import { formatTableTitle } from '../utils/formatTitle';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';

function TaskScheduleExecutionAuditOperationsContent() {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const { organizationId, taskScheduleId, executionId } = useParams();
  const [searchParams] = useSearchParams();
  const assistantId = searchParams.get('assistantId');

  const { getExecutionAuditOperations } = useTaskScheduleApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [auditOperations, setAuditOperations] = useState<AssistantAuditOperationApi[]>([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState<number>(0);

  const [operationId, setOperationId] = useState<string>('');

  const tableTitle = 'Execution Audit Operations';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row: AssistantAuditOperationApi) => row.id, sortable: true },
    {
      name: 'Thread ID',
      selector: (row: AssistantAuditOperationApi) => row.thread_id,
      sortable: true,
    },
    {
      name: 'Duration',
      selector: (row: AssistantAuditOperationApi) => row.duration,
      sortable: true,
    },
    {
      name: 'Created',
      selector: (row: AssistantAuditOperationApi) => formatDateUTC(row.created_at),
      sortable: true,
    },
  ];

  const fetchAuditOperations = async (
    skip: number,
    limit: number,
    opId: string | null = null,
  ): Promise<void> => {
    if (!assistantId || !executionId) {
      setError('Missing assistantId or executionId');
      return;
    }

    try {
      setLoading(true);
      const response = await getExecutionAuditOperations(
        assistantId,
        executionId,
        skip,
        limit,
        undefined,
        undefined,
        opId,
      );
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
    } catch (err) {
      console.error('Error fetching execution audit operations:', err);
      setError('Failed to load audit operations');
      showAlert('fail', 'Failed to load audit operations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByOperationIdChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setOperationId(e.target.value);

  const handleSearchByOperationIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      fetchAuditOperations(persistedSkip, persistedPageSize, operationId);
    }
  };

  const handleSearchByOperationIdClear = (): void => {
    setOperationId('');
    fetchAuditOperations(persistedSkip, persistedPageSize, '');
  };

  const handleRowClick = (row: AssistantAuditOperationApi): void => {
    const qs = assistantId ? `?assistantId=${assistantId}` : '';
    navigate(
      `/organizations/${organizationId}/task-schedule/${taskScheduleId}/executions/${executionId}/audit-operations/${row.id}${qs}`,
    );
  };

  const handleBack = (): void => {
    navigate(`/organizations/${organizationId}/task-schedule/${taskScheduleId}`);
  };

  useEffect(() => {
    fetchAuditOperations(persistedSkip, persistedPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId, executionId]);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="radius-yes p-2 bg-yes col-12">
        <div className="d-flex justify-between align-center">
          <h2 className="h2-title m-0">Execution Audit Operations</h2>
          <div className="buttons-container d-flex justify-end">
            <Button variant="primary" size="small" onClick={handleBack}>
              Back
            </Button>
          </div>
        </div>

        <div className="mb-3 p-2">
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="search" style={{ flex: 1 }}>
              <div className="search-wrapper">
                <div className="icon-wrapper">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </div>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by ID..."
                  value={operationId}
                  onChange={handleSearchByOperationIdChange}
                  onKeyDown={handleSearchByOperationIdKeyDown}
                />
                {operationId && (
                  <div
                    className="icon-wrapper"
                    style={{ cursor: 'pointer' }}
                    onClick={handleSearchByOperationIdClear}
                  >
                    <i className="fa-solid fa-times"></i>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div>Error: {error}</div>
        ) : (
          <Table
            title={tableTitle}
            columns={columns}
            data={auditOperations}
            onRowClicked={handleRowClick}
            selectableRowsHighlight
            progressPending={loading}
            paginationPerPage={persistedPageSize}
            paginationTotalRows={totalAuditOperations}
            sortIcon={<i className="fa-solid fa-chevron-down"></i>}
            onChangePage={fetchAuditOperations}
          />
        )}
      </div>
    </div>
  );
}

function TaskScheduleExecutionAuditOperations() {
  return (
    <TaskScheduleApiProvider>
      <TaskScheduleExecutionAuditOperationsContent />
    </TaskScheduleApiProvider>
  );
}

export default TaskScheduleExecutionAuditOperations;
